import { Link } from "react-router-dom";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import type { Farm } from "@/mocks/farms";

export default function FarmDetailMap({ farm }: { farm: Farm }) {
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(farm.mapQuery)}&z=13&output=embed`;
  const directionsHref = `https://www.google.com/maps?q=${encodeURIComponent(farm.mapQuery)}`;

  return (
    <section id="farm-location" className="sun-glaze bg-secondary-50 py-20 md:py-24">
      <div className="w-full px-4 md:px-8">
        <Reveal className="max-w-3xl mb-10 md:mb-12">
          <div className="flex items-center gap-3 mb-5">
            <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
            <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
              Where It Is
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold">
            Find {farm.name} on the Map
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-stretch">
          <Reveal className="lg:col-span-7">
            <div className="h-full min-h-[24rem] rounded-lg overflow-hidden border border-secondary-200 bg-background-100">
              <iframe
                title={`Map of ${farm.name}`}
                src={mapSrc}
                className="w-full h-full min-h-[24rem]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              ></iframe>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={120}>
            <div className="h-full bg-background-50 rounded-lg border border-secondary-200 p-7 md:p-8 flex flex-col">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent-600 mb-3">
                <i className="ri-map-pin-2-fill"></i>
                {farm.village}
              </span>
              <h3 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold">
                {farm.name}
              </h3>
              <p className="text-sm text-foreground-600 mt-2">
                Hosted by {farm.farmer} · {farm.village}, {farm.district}
              </p>

              <div className="grid grid-cols-2 gap-3 mt-6">
                <div className="bg-background-100 border border-background-200 rounded-md px-4 py-3">
                  <p className="text-xs text-foreground-500">Main crop</p>
                  <p className="text-sm font-semibold text-foreground-900 mt-1">{farm.crop}</p>
                </div>
                <div className="bg-background-100 border border-background-200 rounded-md px-4 py-3">
                  <p className="text-xs text-foreground-500">Farm size</p>
                  <p className="text-sm font-semibold text-foreground-900 mt-1">{farm.area}</p>
                </div>
              </div>

              <p className="text-sm text-foreground-700 leading-relaxed mt-6">
                Planning a visit? Message us and we will help you arrange travel, timings and the
                best season to see {farm.crop} at its finest.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mt-auto pt-6 border-t border-background-200">
                <Link
                  to="/contact"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-5 py-3 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Plan a Visit
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <a
                  href={directionsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-background-100 text-foreground-900 border border-background-300 text-sm font-semibold px-5 py-3 rounded-md hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-navigation-line"></i>
                  Directions
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}